import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Play, X, Maximize } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0b01affe-3e55-4e38-af85-fb6ae72b034b/artifacts/aji67e46_CAKE%20FACTORY_00_LOGO_600px.png";

const TOTAL_SLIDES = 16;

export default function Presentation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gameId = searchParams.get("gameId");
  
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") {
        setCurrentSlide((prev) => Math.min(prev + 1, TOTAL_SLIDES));
      } else if (e.key === "ArrowLeft") {
        setCurrentSlide((prev) => Math.max(prev - 1, 1));
      } else if (e.key === "Escape") {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }
      } else if (e.key === "f" || e.key === "F") {
        toggleFullscreen();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => Math.min(prev + 1, TOTAL_SLIDES));
  const prevSlide = () => setCurrentSlide((prev) => Math.max(prev - 1, 1));

  // Start Game - navigate to TV Display in same tab
  const startGame = () => {
    if (gameId) {
      // Navigate to TV Display for this game (same tab)
      window.location.href = `/display/${gameId}`;
    } else {
      // No game ID, go to home to create a game
      navigate("/");
    }
  };

  const slideUrl = `/slides/slide_${currentSlide.toString().padStart(2, "0")}.png`;

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header - hide in fullscreen */}
      {!isFullscreen && (
        <header className="flex items-center justify-between px-6 py-4 bg-[#2E1065]">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Cake Factory" className="w-12 h-12" />
            <h1 className="font-heading text-2xl text-white">Cake Factory</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/60 font-mono">
              Slide {currentSlide} / {TOTAL_SLIDES}
            </span>
            <button
              onClick={startGame}
              className="px-4 py-2 bg-[#D946EF] text-white rounded-lg font-semibold hover:bg-[#A21CAF] transition-colors flex items-center gap-2"
              data-testid="start-game-btn"
            >
              <Play className="w-5 h-5" />
              Start Game
            </button>
          </div>
        </header>
      )}

      {/* Slide Display */}
      <main 
        className="flex-1 flex items-center justify-center relative cursor-pointer"
        onClick={nextSlide}
      >
        <img
          src={slideUrl}
          alt={`Slide ${currentSlide}`}
          className="max-w-full max-h-full object-contain"
          data-testid="current-slide"
        />

        {/* Navigation Buttons */}
        <button
          onClick={(e) => { e.stopPropagation(); prevSlide(); }}
          disabled={currentSlide === 1}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="prev-slide-btn"
        >
          <ChevronLeft className="w-8 h-8 text-white" />
        </button>

        <button
          onClick={(e) => { e.stopPropagation(); nextSlide(); }}
          disabled={currentSlide === TOTAL_SLIDES}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          data-testid="next-slide-btn"
        >
          <ChevronRight className="w-8 h-8 text-white" />
        </button>

        {/* Top controls */}
        <div className="absolute top-4 right-4 flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
            className="w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors"
            data-testid="fullscreen-btn"
            title="Fullscreen (F)"
          >
            {isFullscreen ? (
              <X className="w-5 h-5 text-white" />
            ) : (
              <Maximize className="w-5 h-5 text-white" />
            )}
          </button>
        </div>

        {/* Start Game button in fullscreen */}
        {isFullscreen && (
          <button
            onClick={(e) => { e.stopPropagation(); startGame(); }}
            className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-[#D946EF] text-white rounded-lg font-semibold hover:bg-[#A21CAF] transition-colors flex items-center gap-2"
            data-testid="start-game-fullscreen-btn"
          >
            <Play className="w-5 h-5" />
            Start Game
          </button>
        )}

        {/* Slide counter in fullscreen */}
        {isFullscreen && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-4 py-2 rounded-full">
            <span className="text-white/80 font-mono">
              {currentSlide} / {TOTAL_SLIDES}
            </span>
          </div>
        )}
      </main>

      {/* Footer with slide dots - hide in fullscreen */}
      {!isFullscreen && (
        <footer className="flex items-center justify-center gap-2 py-4 bg-[#2E1065]">
          {Array.from({ length: TOTAL_SLIDES }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i + 1)}
              className={`w-3 h-3 rounded-full transition-colors ${
                currentSlide === i + 1
                  ? "bg-[#D946EF]"
                  : "bg-white/30 hover:bg-white/50"
              }`}
              data-testid={`slide-dot-${i + 1}`}
            />
          ))}
        </footer>
      )}

      {/* Instructions */}
      {!isFullscreen && (
        <div className="text-center text-white/40 text-sm pb-4 bg-[#2E1065]">
          Use arrow keys or click to navigate • Press F for fullscreen
        </div>
      )}
    </div>
  );
}
